import { useEffect, useState } from 'react';
import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { PreviewExercise } from '@models/exercise';
import { Path } from '@models/path';
import Hero from '@components/Hero';
import CardExercise from '@components/CardExercise';
import LayoutPage from '@components/LayoutPage';
import BackButton from '@components/BackButton';
import { getExercisesByChallenge } from '@services/challenge';
import { getPath } from '@fetchers/paths';

interface Props {
  path: Path;
}

export default function Challenges({ path }: Props) {
  const { t } = useTranslation('common');
  const [newExercises, setNewExercises] = useState<PreviewExercise[]>([]);

  useEffect(() => {
    const localExercises = getExercisesByChallenge(path.id);
    if (localExercises) {
      const newExercisesDone = path?.exercises.map((item) => {
        return {
          ...item,
          done: localExercises.find((local) => local.exerciseId === `${item.lang}/${item.id}`)
            ? true
            : false,
        };
      });
      setNewExercises(newExercisesDone);
    } else {
      setNewExercises(path?.exercises);
    }
  }, [path]);

  if (!path) {
    return <p>{t('challenges.loading')}</p>;
  }

  return (
    <>
      <Head>
        <title>{path.title} | Super Hero</title>
        <meta name="description" content={path.description} />
      </Head>
      <LayoutPage>
        <BackButton
          link={`/langs/${path.langId}`}
          text={t('path.detail.back', { text: path.lang.title })}
        />
        <Hero
          title={path?.title}
          text={path?.description}
          icon={`/icons/${path.icon}-dynamic-color.png`}
        />
        <div className="mt-6 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8">
          {newExercises.map((item, index) => (
            <CardExercise
              exercise={item}
              key={index}
              state={item.done ? 'done' : 'undone'}
              index={index + 1}
              link={`/e/${item.lang}/${item.id}?path=${item.pathId}`}
            />
          ))}
        </div>
      </LayoutPage>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, locale, res }) => {
  const { langId, pathId } = params;
  if (typeof pathId === 'string' && typeof langId === 'string') {
    const path = await getPath(pathId, langId);
    const ssrConfig = await serverSideTranslations(locale, ['common']);
    res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');
    return {
      props: {
        path,
        ...ssrConfig,
      },
    };
  }
  return {
    notFound: true,
  };
};
